import { useState } from 'react';
import { ArrowUpRight, Github } from 'lucide-react';
import { useTeamStats, type StatsPrRef } from '../../hooks/useTeamStats';
import { RdCardDialog, type RdCardConfig, type RdCardItem } from './RdCardDialog';
import { askNavTarget, askPrefillFor, EXIT_LABEL } from '../../lib/brief-card-exits';

/*
 * GithubStatsBoard — the mock's dark "GitHub stats / How the codebase is moving" board (Teddy
 * 2026-07-27: render it from the real stats rail), an HONEST subset of the mock:
 *
 *  - StatTiles in flight / merged / ready map 1:1 to the rail (prsInFlight / mergedThisWeek /
 *    readyToMerge). The mock's decorative sparklines/bars are fixture art — no per-day series
 *    exists on the rail, so no charts are faked.
 *  - The mock's deploy-frequency tile and churn-% dial have NO rail (no deploy/CI mirror; no
 *    active-code denominator) and are ruled never-fake — churn renders its real components
 *    (+adds −dels · files · commits) instead.
 *  - Traceability (Teddy 2026-07-28): every number is clickable evidence — the rail now carries
 *    the capped PR lists behind each tile, so a tile opens the PRs it counts (each deep-linking
 *    to GitHub), and the mock's lower-board slot renders a REAL recent-merges list instead of
 *    dead space. A pre-traceability payload (no lists) renders plain tiles, never an empty popup.
 *  - Contributions subtitle says what the number actually is (commits & merges — reviews and
 *    comments are not mirrored), unlike the mock's fixture copy.
 *  - Badge is the rail's trailing window ("past 7 days"), not the mock's WEEK TO DATE.
 *
 * The board never collapses (static section sizing): loading shows ghost rows, an error or an
 * unconnected GitHub shows an honest line inside the same shell.
 */

function GhostRow({ w }: { w: string }) {
  return <span className={`block h-4 ${w} animate-pulse rounded bg-white/10`} />;
}

const prRefLabel = (p: StatsPrRef) => `${p.repoFullName}#${p.number}`;

const prRefItems = (refs: StatsPrRef[]): RdCardItem[] =>
  refs.map(p => ({
    title: p.title || prRefLabel(p),
    note: [prRefLabel(p), p.author].filter(Boolean).join(' · '),
    href: p.url ?? null,
    // C1: the in-app exit the stats board never had. Nullish by contract — a row read before the
    // MVP arm stamps it simply keeps its GitHub link and grows no note verb.
    conversationId: p.conversationId ?? null,
  }));

/** The drill-in behind a stat tile: the PRs the number counts. Honest about the cap. */
function tileCard(
  title: string,
  count: number,
  refs: StatsPrRef[],
  exits: { onNavNote: (conversationId: string) => void; onAsk: (prefill: string) => void },
): RdCardConfig {
  return {
    icon: <Github size={19} />,
    eyebrow: 'GitHub stats',
    title,
    description: count > refs.length ? `Showing the ${refs.length} most recently updated of ${count}.` : null,
    itemsLabel: 'The pull requests',
    items: prRefItems(refs),
    actions: [],
    // A tile counts many PRs, so it has no single note and no single artifact — the rows carry
    // those. What it does have is the contract's third exit, which needs no ids at all.
    primary: {
      sourceTargets: null,
      externalUrl: null,
      noteConversationId: null,
      askPrefill: askPrefillFor(title),
      onNavNote: exits.onNavNote,
      onAsk: exits.onAsk,
    },
    onNavNote: exits.onNavNote,
  };
}

function StatTile({ value, label, onOpen }: { value: number; label: string; onOpen?: () => void }) {
  const inner = (
    <>
      <span className="font-['Instrument_Serif'] text-[34px] leading-7">{value}</span>
      <span className="mt-1 block text-[10px] text-[#c7c2d4]">{label}</span>
    </>
  );
  if (!onOpen) return <div className="rounded-2xl bg-white/[.075] p-3">{inner}</div>;
  return (
    <button
      onClick={onOpen}
      className="rounded-2xl bg-white/[.075] p-3 text-left transition hover:-translate-y-0.5 hover:bg-white/[.12]"
      data-testid={`gh-tile-${label.replace(/\s+/g, '-')}`}
    >
      {inner}
    </button>
  );
}

export function GithubStatsBoard({ onNav }: { onNav: (id: string) => void }) {
  // isPending, not isLoading: a paused (offline-mount) query must show the skeleton, never the
  // false 'Nothing wired' claim (adversarial-review finding, 2026-07-27).
  const { data, isPending, error } = useTeamStats();
  const [openCard, setOpenCard] = useState<RdCardConfig | null>(null);
  const github = data?.status === 'ready' ? data.roleStats?.github : null;
  // C1: the board joins the brief's one click contract. Ask never auto-submits (/ask?q= prefills).
  const exits = {
    onNavNote: (conversationId: string) => onNav('conversations/' + conversationId),
    onAsk: (prefill: string) => onNav(askNavTarget(prefill)),
  };

  let body;
  if (isPending) {
    body = (
      <div className="mt-5 space-y-3">
        <div className="grid grid-cols-3 gap-2">
          {[0, 1, 2].map(i => (
            <div key={i} className="space-y-2 rounded-2xl bg-white/[.075] p-3">
              <GhostRow w="w-10" />
              <GhostRow w="w-16" />
            </div>
          ))}
        </div>
        <div className="rounded-2xl border border-white/10 bg-white/[.055] p-3">
          <GhostRow w="w-2/3" />
        </div>
      </div>
    );
  } else if (error || !github || data?.status !== 'ready' || !github.connected) {
    body = (
      <p className="mt-5 rounded-2xl border border-dashed border-white/20 p-4 text-sm text-[#c2bcd2]">
        {error
          ? 'Stats are unavailable right now.'
          : 'Nothing wired - connect GitHub to see how the codebase is moving.'}
      </p>
    );
  } else {
    const inFlightPrs = github.inFlightPrs ?? [];
    const readyPrs = github.readyPrs ?? [];
    const mergedPrs = github.mergedPrs ?? [];
    const openTile = (title: string, count: number, refs: StatsPrRef[]) =>
      refs.length > 0 ? () => setOpenCard(tileCard(title, count, refs, exits)) : undefined;
    body = (
      <>
        <div className="mt-5 grid grid-cols-3 gap-2">
          <StatTile
            value={github.prsInFlight}
            label="in flight"
            onOpen={openTile(`${github.prsInFlight} PR${github.prsInFlight === 1 ? '' : 's'} in flight`, github.prsInFlight, inFlightPrs)}
          />
          <StatTile
            value={github.mergedThisWeek}
            label="merged"
            onOpen={openTile(`${github.mergedThisWeek} merged this week`, github.mergedThisWeek, mergedPrs)}
          />
          <StatTile
            value={github.readyToMerge}
            label="ready"
            onOpen={openTile(`${github.readyToMerge} ready to merge`, github.readyToMerge, readyPrs)}
          />
        </div>
        <div className="mt-3 rounded-2xl border border-white/10 bg-white/[.06] p-3">
          <span className="text-xs text-[#c7c2d4]">Code churn</span>
          <span className="mt-2 block font-['Instrument_Serif'] text-2xl leading-6">
            +{github.churn.additions} <span className="text-[#c7c2d4]">−{github.churn.deletions}</span>
          </span>
          <span className="mt-1 block text-[10px] text-[#c7c2d4]">
            {github.churn.changedFiles} files · {github.churn.commitCount} commits
          </span>
        </div>
        {/* The mock's lower-board slot, filled with REAL rows instead of the never-fake deploy
          * tile: the week's merges, each a direct GitHub link. Renders only when merges exist. */}
        {mergedPrs.length > 0 && (
          <div className="mt-3 rounded-2xl border border-white/10 bg-white/[.055] p-3" data-testid="gh-recent-merges">
            <span className="text-xs text-[#c7c2d4]">Recent merges</span>
            <div className="mt-2 space-y-1">
              {/* C1: each row keeps its GitHub link AND gains the in-app exit when the row carries a
                * note. The note verb is a sibling of the anchor, not a child — a button inside an
                * <a> is invalid, and nesting it would make the whole row navigate off-app. */}
              {mergedPrs.slice(0, 4).map(p => (
                <div key={`${p.repoFullName}#${p.number}`} className="flex items-baseline gap-1">
                  <a
                    href={p.url ?? undefined}
                    target="_blank"
                    rel="noreferrer"
                    className="group flex min-w-0 flex-1 items-baseline gap-2 rounded-lg px-1.5 py-1 text-sm transition hover:bg-white/[.08]"
                  >
                    <span className="shrink-0 font-['DM_Mono'] text-[10px] text-white/45">{prRefLabel(p)}</span>
                    <span className="min-w-0 flex-1 truncate text-[#e6e1f0]">{p.title || 'Untitled PR'}</span>
                    <ArrowUpRight size={12} className="shrink-0 text-white/35 transition group-hover:text-[#ded7ec]" />
                  </a>
                  {p.conversationId && (
                    <button
                      type="button"
                      onClick={() => exits.onNavNote(p.conversationId as string)}
                      className="shrink-0 rounded-lg px-1.5 py-1 text-[11px] font-medium text-white/45 transition hover:bg-white/[.08] hover:text-[#ded7ec]"
                    >
                      {EXIT_LABEL.note}
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
        <div className="mt-3 flex w-full items-center gap-3 rounded-xl border border-white/10 bg-white/[.055] p-3">
          <span className="grid size-9 place-items-center rounded-lg bg-white/10">
            <Github size={17} />
          </span>
          <span className="flex-1">
            <span className="block text-sm font-medium">
              {github.contributions.total} contribution{github.contributions.total === 1 ? '' : 's'}
            </span>
            <span className="mt-0.5 block text-xs text-[#c7c2d4]">
              commits &amp; merges · {github.contributions.authors} author
              {github.contributions.authors === 1 ? '' : 's'}
            </span>
          </span>
        </div>
      </>
    );
  }

  return (
    <>
      <section
        className="relative h-full overflow-hidden rounded-[24px] border border-[#2a2447] bg-[#322b4d] p-5 text-[#fbf5e9] shadow-[0_18px_38px_rgba(74,58,107,.16)] sm:p-6"
        data-testid="github-stats-board"
      >
        <div className="absolute -right-7 -top-8 size-36 rounded-full border-[18px] border-white/[.05]" />
        <div className="relative">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <span className="grid size-10 place-items-center rounded-xl bg-white/10">
                <Github size={19} />
              </span>
              <div>
                <p className="font-['DM_Mono'] text-[10px] uppercase tracking-[.18em] text-[#c7c2d4]">GitHub stats</p>
                <h3 className="font-semibold">How the codebase is moving</h3>
              </div>
            </div>
            <span className="rounded-full bg-[#ded7ec] px-2.5 py-1 font-['DM_Mono'] text-[9px] text-[#2e2748]">
              PAST {data?.status === 'ready' && github ? github.windowDays : 7} DAYS
            </span>
          </div>
          {body}
        </div>
      </section>
      {openCard && <RdCardDialog config={openCard} onClose={() => setOpenCard(null)} />}
    </>
  );
}
